// src/client/renderer/vfx/env/waterfall-mist-pool.ts
//
// Plan 18, Task 4.2 — Waterfall mist ambient pool.
//
// Behaviour contract:
//   - ALWAYS-ON ambient pool with INTERNAL spawner. mode: 'ambient'.
//   - Depends on an optional `getFountainAnchors?(): readonly THREE.Vector3[]`
//     callback supplied by Plan 19. When the callback is missing or returns [],
//     the pool is a graceful no-op (acquire returns null, update is empty).
//   - Pool's internal spawner emits ~one mist plume per anchor every 0.6-1.0
//     seconds (random per-anchor interval, deterministic via LCG). Each plume
//     is a small additive PlaneGeometry (0.6 × 1.0) with white-blue tint
//     (0xc8e6ff), opacity 0→0.6→0 over a 1.8-second lifetime. The plume rises
//     ~3 units along Y while spreading laterally ~0.5 units (jittered).
//   - Max-active = 24 across all anchors; 25th would-be plume is silently
//     dropped this tick.
//   - isFinished(plume) returns true once elapsed > 1.8s.
//   - dispose() disposes pool-owned geometry + per-plume material clones once.
//
// Implementation notes:
//   - ONE shared PlaneGeometry(0.6, 1.0). Per-plume Mesh instances use the
//     shared geometry + a CLONED material (so per-plume opacity is independent).
//   - Plume animation: gsap timeline from registry.create(). Position tween:
//     y+=3, x±0.5 jitter, z±0.5 jitter. Opacity: 0→0.6 over 0.4s, 0.6→0 over
//     1.4s. tl.call(fn, undefined, 1.8) marks finished (NEVER eventCallback).
//   - Deterministic RNG: LCG seeded at pool creation (optional injection).
//   - Spawner handle: a THREE.Group returned by acquire(), added to scene.
//   - Per-anchor accumulator + per-anchor next-spawn-interval.
//   - dispose() walks active plumes, disposes per-plume material clones, then
//     disposes the shared geometry + template material once each.

import * as THREE from 'three';
import type {
  EnvCeremonyPool,
  EnvCeremonyPoolDeps,
  EnvCeremonySpawnArgs,
} from './env-ceremony-types';
import type { GsapRegistry } from '../gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Extended deps — optional getFountainAnchors callback + optional seed
// ─────────────────────────────────────────────────────────────────────────────

export interface WaterfallMistPoolConfig extends EnvCeremonyPoolDeps {
  /** Plan 19 wire-up supplies this; absent → graceful no-op. */
  readonly getFountainAnchors?: () => readonly THREE.Vector3[];
  /** Optional deterministic RNG seed (defaults to a fixed value for tests). */
  readonly seed?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** PlaneGeometry dimensions for mist plumes. */
const PLUME_WIDTH = 0.6;
const PLUME_HEIGHT = 1.0;

/** White-blue mist tint. */
const PLUME_COLOUR = 0xc8e6ff;

/** Peak opacity of a plume at mid-life. */
const PLUME_PEAK_OPACITY = 0.6;

/** Plume lifetime (seconds). */
const PLUME_TTL = 1.8;

/** Opacity ramp-up duration (seconds). */
const PLUME_FADE_IN = 0.4;

/** Opacity ramp-down duration (seconds). */
const PLUME_FADE_OUT = 1.4;

/** Vertical rise during lifetime (units). */
const PLUME_RISE = 3.0;

/** Lateral spread half-range (units). */
const PLUME_LATERAL = 0.5;

/** Maximum simultaneously active plumes (across all anchors). */
const MAX_ACTIVE = 24;

/** Minimum spawn interval per anchor (seconds). */
const SPAWN_INTERVAL_MIN = 0.6;

/** Maximum spawn interval per anchor (seconds). */
const SPAWN_INTERVAL_MAX = 1.0;

// ─────────────────────────────────────────────────────────────────────────────
// LCG RNG (deterministic)
// ─────────────────────────────────────────────────────────────────────────────

function makeLcg(seed: number): () => number {
  let state = seed;
  return function next(): number {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-plume state
// ─────────────────────────────────────────────────────────────────────────────

interface PlumeState {
  mesh: THREE.Mesh;
  elapsed: number;
  finished: boolean;
  tlHandle: ReturnType<GsapRegistry['create']>;
  /** Start world position (for position tween target computation). */
  startX: number;
  startY: number;
  startZ: number;
  /** Jittered end position offsets. */
  endX: number;
  endY: number;
  endZ: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createWaterfallMistPool({
  scene,
  registry,
  getFountainAnchors,
  seed = 8675309,
}: WaterfallMistPoolConfig): EnvCeremonyPool {
  const rng = makeLcg(seed);

  /** Shared geometry — disposed once in dispose(). */
  const sharedGeometry = new THREE.PlaneGeometry(PLUME_WIDTH, PLUME_HEIGHT);

  /** Template material — cloned per-plume; disposed once in dispose(). */
  const templateMaterial = new THREE.MeshBasicMaterial({
    color: PLUME_COLOUR,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  /** Active plume states. Keyed by Mesh (the emitter token). */
  const activePlumes = new Map<THREE.Object3D, PlumeState>();

  /** Spawner handles returned by acquire(). */
  const spawnerHandles = new Set<THREE.Object3D>();

  /** Per-anchor time accumulator (time until next plume spawn for that anchor).
   *  Key: anchor index (number). */
  const anchorAccumulators = new Map<number, number>();

  let disposed = false;

  // ─────────────────────────────────────────────────────────────────────────
  // Internal helpers
  // ─────────────────────────────────────────────────────────────────────────

  function nextInterval(): number {
    return SPAWN_INTERVAL_MIN + rng() * (SPAWN_INTERVAL_MAX - SPAWN_INTERVAL_MIN);
  }

  function jitter(): number {
    return (rng() - 0.5) * 2.0 * PLUME_LATERAL;
  }

  function spawnPlume(anchor: THREE.Vector3): void {
    if (activePlumes.size >= MAX_ACTIVE) return;

    const startX = anchor.x;
    const startY = anchor.y;
    const startZ = anchor.z;

    const endX = startX + jitter();
    const endY = startY + PLUME_RISE;
    const endZ = startZ + jitter();

    // Per-plume material clone (own opacity channel)
    const material = templateMaterial.clone();
    material.opacity = 0;

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(startX, startY, startZ);
    scene.add(mesh);

    const tlHandle = registry.create('waterfall_mist');
    const tl = tlHandle.timeline;

    // Opacity proxy to drive per-plume opacity (GSAP needs plain object target)
    const opProxy = { value: 0.0 };

    // Opacity 0 → peak (ramp up)
    tl.to(
      opProxy,
      {
        value: PLUME_PEAK_OPACITY,
        duration: PLUME_FADE_IN,
        ease: 'power1.in',
        onUpdate: () => {
          material.opacity = opProxy.value;
        },
      },
      0,
    );

    // Opacity peak → 0 (ramp down)
    tl.to(
      opProxy,
      {
        value: 0.0,
        duration: PLUME_FADE_OUT,
        ease: 'power1.out',
        onUpdate: () => {
          material.opacity = opProxy.value;
        },
      },
      PLUME_FADE_IN,
    );

    // Position rise tween (parallel with opacity)
    tl.to(
      mesh.position,
      {
        x: endX,
        y: endY,
        z: endZ,
        duration: PLUME_TTL,
        ease: 'none',
      },
      0,
    );

    const plumeState: PlumeState = {
      mesh,
      elapsed: 0,
      finished: false,
      tlHandle,
      startX,
      startY,
      startZ,
      endX,
      endY,
      endZ,
    };

    // Mark finished via tl.call at TTL position (NEVER eventCallback)
    tl.call(
      () => {
        plumeState.finished = true;
      },
      undefined,
      PLUME_TTL,
    );

    activePlumes.set(mesh, plumeState);
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(_args: EnvCeremonySpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // No-op: missing or empty anchors
    if (!getFountainAnchors) return null;
    const anchors = getFountainAnchors();
    if (anchors.length === 0) return null;

    // Initialize per-anchor accumulators (if not already set from a prior acquire)
    for (let i = 0; i < anchors.length; i++) {
      if (!anchorAccumulators.has(i)) {
        anchorAccumulators.set(i, nextInterval());
      }
    }

    const spawnerGroup = new THREE.Group();
    scene.add(spawnerGroup);
    spawnerHandles.add(spawnerGroup);
    return spawnerGroup;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (disposed) return new Set();

    // No-op: no spawner handles active (no acquire has been called with valid anchors)
    if (spawnerHandles.size === 0) return new Set();

    // Fetch current anchors
    const anchors = getFountainAnchors ? getFountainAnchors() : [];

    // Advance per-anchor accumulators; spawn plumes when accumulator expires
    for (let i = 0; i < anchors.length; i++) {
      const acc = anchorAccumulators.get(i) ?? nextInterval();
      const newAcc = acc - deltaSeconds;
      if (newAcc <= 0) {
        spawnPlume(anchors[i]);
        anchorAccumulators.set(i, nextInterval());
      } else {
        anchorAccumulators.set(i, newAcc);
      }
    }

    // Advance active plume timelines + check for completion
    const toRemove: THREE.Object3D[] = [];
    for (const [mesh, state] of activePlumes) {
      state.elapsed += deltaSeconds;
      state.tlHandle.timeline.time(state.elapsed);
      if (state.elapsed >= PLUME_TTL || state.finished) {
        state.finished = true;
        toRemove.push(mesh);
      }
    }

    // Clean up finished plumes
    for (const mesh of toRemove) {
      const state = activePlumes.get(mesh);
      if (state) {
        state.tlHandle.dispose();
        scene.remove(mesh);
        // Dispose per-plume material clone
        if ((mesh as THREE.Mesh).material instanceof THREE.Material) {
          ((mesh as THREE.Mesh).material as THREE.Material).dispose();
        }
        activePlumes.delete(mesh);
      }
    }

    // Build active set
    const active = new Set<THREE.Object3D>();
    for (const handle of spawnerHandles) {
      active.add(handle);
    }
    for (const mesh of activePlumes.keys()) {
      active.add(mesh);
    }
    return active;
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    if (spawnerHandles.has(emitter)) return false;

    const state = activePlumes.get(emitter);
    if (state) return state.finished;

    // Already removed from active set → finished
    return true;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (spawnerHandles.has(emitter)) {
      scene.remove(emitter);
      spawnerHandles.delete(emitter);
      return;
    }

    const state = activePlumes.get(emitter);
    if (state) {
      state.tlHandle.dispose();
      scene.remove(emitter);
      ((emitter as THREE.Mesh).material as THREE.Material).dispose();
      activePlumes.delete(emitter);
    }
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Dispose all active plumes (per-plume material clones)
    for (const [mesh, state] of activePlumes) {
      state.tlHandle.dispose();
      scene.remove(mesh);
      ((mesh as THREE.Mesh).material as THREE.Material).dispose();
    }
    activePlumes.clear();

    // Remove spawner handles
    for (const handle of spawnerHandles) {
      scene.remove(handle);
    }
    spawnerHandles.clear();
    anchorAccumulators.clear();

    // Dispose shared resources exactly once
    sharedGeometry.dispose();
    templateMaterial.dispose();
  }

  return { mode: 'ambient', acquire, update, isFinished, release, dispose };
}
